/**
 * Owner Booking Approval page.
 * Lists submitted bookings pending approval with approve/reject actions.
 *
 * Requirements: 13.2, 13.3, 13.4, 13.5, 20.3, 20.4
 */

import { createDataGrid } from '../components/data-grid.js';
import { getFlights, transitionFlightStatus } from '../services/api.js';
import { getUser, isOwner } from '../services/auth.js';
import { formatDateUK } from '../utils/format.js';
import { FLIGHT_STATUS } from '../utils/constants.js';
import { openDetailModal } from '../components/detail-modal.js';

// ── Exported pure helpers for testability ─────────────

/**
 * Filter bookings to only those with SUBMITTED status (pending approval).
 * @param {Object[]} bookings
 * @returns {Object[]}
 */
export function filterPendingBookings(bookings) {
  if (!Array.isArray(bookings)) return [];
  return bookings.filter((b) => b && b.status === FLIGHT_STATUS.SUBMITTED);
}

/**
 * Format a booking for the approval grid display.
 * @param {Object} booking
 * @returns {Object}
 */
export function formatBookingForApproval(booking) {
  if (!booking) return {};
  return {
    ...booking,
    display_aircraft: booking.aircraft?.registration || '—',
    display_pilot: booking.pilots?.name || '—',
    display_date: booking.flight_date ? formatDateUK(booking.flight_date) : '—',
    display_start: (booking.start_time || '').slice(0, 5),
    display_end: (booking.end_time || '').slice(0, 5),
    display_purpose: booking.purpose || '—',
  };
}

// ── Page state ────────────────────────────────────────

let allBookings = [];

// ── Main render ───────────────────────────────────────

export async function renderOwnerBookings() {
  const app = document.getElementById('app');
  if (!app) return;

  if (!isOwner()) {
    app.innerHTML = `
      <div class="container">
        <h2>Access Denied</h2>
        <p>Only owners can access the booking approval page.</p>
      </div>
    `;
    return;
  }

  app.innerHTML = `
    <div class="container">
      <h2>Booking Approvals</h2>
      <div id="owner-bookings-msg"></div>
      <div class="card">
        <h3>Pending Bookings</h3>
        <div id="pending-bookings-grid">Loading…</div>
      </div>
    </div>
  `;

  try {
    await loadPendingBookings();
  } catch (err) {
    showMsg(`Failed to load bookings: ${err.message}`, true);
  }
}

// ── Load and render pending bookings ──────────────────

async function loadPendingBookings() {
  const container = document.getElementById('pending-bookings-grid');
  if (!container) return;

  try {
    const raw = await getFlights({ statusIn: [FLIGHT_STATUS.SUBMITTED, FLIGHT_STATUS.APPROVED] });
    allBookings = raw;
    const pending = filterPendingBookings(raw);
    renderPendingGrid(container, pending);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load bookings: ${err.message}</p>`;
  }
}

function renderPendingGrid(container, bookings) {
  container.innerHTML = '';

  const columns = [
    { key: 'flight_date', label: 'Date', format: (v) => formatDateUK(v) },
    {
      key: 'aircraft_reg',
      label: 'Aircraft',
      html: true,
      format: (_, row) => {
        const reg = row.aircraft?.registration;
        const id = row.aircraft?.id || row.aircraft_id;
        if (!reg) return '—';
        if (!id) return reg;
        return `<a href="#/aircraft/${id}" class="aircraft-link">${reg}</a>`;
      },
    },
    {
      key: 'pilot_name',
      label: 'Pilot',
      format: (_, row) => row.pilots?.name || '—',
    },
    { key: 'start_time', label: 'Start', format: (v) => (v || '').slice(0, 5) },
    { key: 'end_time', label: 'End', format: (v) => (v || '').slice(0, 5) },
    { key: 'purpose', label: 'Purpose', format: (v) => v || '—' },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      html: true,
      format: (_, row) => {
        return `<button class="btn btn-sm btn-approve-booking" data-id="${row.id}">Approve</button> `
          + `<button class="btn btn-sm btn-danger btn-reject-booking" data-id="${row.id}">Reject</button>`;
      },
    },
  ];

  const grid = createDataGrid({
    columns,
    data: bookings,
    defaultSort: 'flight_date',
    defaultOrder: 'asc',
    emptyMessage: 'No pending bookings',
    filterable: true,
  });

  container.appendChild(grid);

  // Event delegation for approve/reject buttons
  container.addEventListener('click', async (e) => {
    const approveBtn = e.target.closest('.btn-approve-booking');
    if (approveBtn) {
      await handleApprove(approveBtn.dataset.id);
      return;
    }

    const rejectBtn = e.target.closest('.btn-reject-booking');
    if (rejectBtn) {
      await handleReject(rejectBtn.dataset.id);
    }
  });
}

// ── Approve / Reject handlers ─────────────────────────

async function handleApprove(bookingId) {
  try {
    await transitionFlightStatus(bookingId, FLIGHT_STATUS.APPROVED);
    showMsg('Booking approved.');
    await loadPendingBookings();
  } catch (err) {
    showMsg(`Failed to approve booking: ${err.message}`, true);
  }
}

async function handleReject(bookingId) {
  openDetailModal({
    title: 'Reject Booking',
    fields: [
      { name: 'reject_reason', label: 'Reason for Rejection', type: 'textarea', required: true },
    ],
    values: { reject_reason: '' },
    saveLabel: 'Reject',
    onSave: async (formData) => {
      const reason = (formData.reject_reason || '').trim();
      if (!reason) throw new Error('Rejection reason is required');
      await transitionFlightStatus(bookingId, FLIGHT_STATUS.REJECTED, { reject_reason: reason });
      showMsg('Booking rejected.');
      await loadPendingBookings();
    },
  });
}

// ── UI helpers ────────────────────────────────────────

function showMsg(text, isError = false) {
  const el = document.getElementById('owner-bookings-msg');
  if (!el) return;
  el.textContent = text;
  el.className = isError ? 'error' : 'success';
  setTimeout(() => {
    el.textContent = '';
    el.className = '';
  }, 5000);
}
